import { type ReactElement } from 'react';
import { Stack } from '@/ui';
import { Box, FilterListOutlinedIcon, PanelHeadingText } from '@/ui/primitives';
import { MetricsToolbarCollapsedControls } from './MetricsToolbarCollapsedControls';
import { MetricsToolbarFilters } from './MetricsToolbarFilters';
import { MetricsToolbarSearchControl } from './MetricsToolbarSearchControl';
import type { MetricsToolbarTagOption } from './metricsToolbarTagOptions';
import type { TableDropdownOption, TableStatusOption } from '../tableSystem/tableToolbar.types';
import type { RefObject } from 'react';

type MetricsToolbarInlineClusterProps<TStatus extends string> = {
  title: string;
  showInlineFilters: boolean;
  useCollapsedToolbarControls: boolean;
  toolbarControlSize: number;
  hasActiveInlineFilterState: boolean;
  useWrappedHeaderLayout: boolean;
  popoverState: { type: string | null; anchorEl: HTMLElement | null };
  openPopover: (type: 'filters' | 'date') => (event: React.MouseEvent<HTMLElement>) => void;
  closePopover: () => void;
  searchValue: string;
  searchAriaLabel: string;
  setSearchValue: (value: string) => void;
  searchInputRef: RefObject<HTMLInputElement | null>;
  handleSearchBlur: () => void;
  handleSearchKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  hasActiveFilters: boolean;
  statusOptions: Array<TableStatusOption<TStatus>>;
  statusFilterLabel: string;
  filterStatuses: TStatus[];
  onFilterStatusesChange: (next: TStatus[]) => void;
  filterEntities: string[];
  onFilterEntitiesChange?: (next: string[]) => void;
  entityOptions?: TableDropdownOption[];
  entityLabel?: string;
  filterItems: string[];
  onFilterItemsChange?: (next: string[]) => void;
  itemOptions?: TableDropdownOption[];
  itemLabel?: string;
  filterSuites: string[];
  onFilterSuitesChange?: (next: string[]) => void;
  suiteOptions?: TableDropdownOption[];
  suiteLabel?: string;
  filterTags: string[];
  onFilterTagsChange: (next: string[]) => void;
  availableTags: MetricsToolbarTagOption[];
  filterProjects?: string[];
  onFilterProjectsChange?: (next: string[]) => void;
  projectOptions?: TableDropdownOption[];
  preserveWindowScroll: (action: () => void) => void;
  toolbarInactiveIconColor: string;
  tooltipSlotProps: {
    tooltip: {
      sx: {
        fontSize: string;
        lineHeight: number;
      };
    };
  };
  showSearch: boolean;
  toolbarExpandedSearchWidth: number;
  toolbarSearchWidthPx: string;
};

export const MetricsToolbarInlineCluster = <TStatus extends string>({
  title,
  showInlineFilters,
  useCollapsedToolbarControls,
  toolbarControlSize,
  hasActiveInlineFilterState,
  useWrappedHeaderLayout,
  popoverState,
  openPopover,
  closePopover,
  searchValue,
  searchAriaLabel,
  setSearchValue,
  searchInputRef,
  handleSearchBlur,
  handleSearchKeyDown,
  hasActiveFilters,
  statusOptions,
  statusFilterLabel,
  filterStatuses,
  onFilterStatusesChange,
  filterEntities,
  onFilterEntitiesChange,
  entityOptions,
  entityLabel,
  filterItems,
  onFilterItemsChange,
  itemOptions,
  itemLabel,
  filterSuites,
  onFilterSuitesChange,
  suiteOptions,
  suiteLabel,
  filterTags,
  onFilterTagsChange,
  availableTags,
  filterProjects,
  onFilterProjectsChange,
  projectOptions,
  preserveWindowScroll,
  toolbarInactiveIconColor,
  tooltipSlotProps,
  showSearch,
  toolbarExpandedSearchWidth,
  toolbarSearchWidthPx,
}: MetricsToolbarInlineClusterProps<TStatus>): ReactElement => (
  <Stack direction="row" alignItems="center" gap="xs" sx={{ minWidth: 0 }}>
    {showInlineFilters ? (
      !useCollapsedToolbarControls ? (
        <Box
          component="span"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: toolbarControlSize,
            height: toolbarControlSize,
            minWidth: toolbarControlSize,
            flexShrink: 0,
            color: hasActiveInlineFilterState ? 'primary.main' : 'action.disabled',
          }}
        >
          <FilterListOutlinedIcon
            sx={{
              width: '22px !important',
              height: '22px !important',
            }}
          />
        </Box>
      ) : (
        <MetricsToolbarCollapsedControls
          filtersOpen={popoverState.type === 'filters'}
          filtersAnchorEl={popoverState.anchorEl}
          onOpenFilters={openPopover('filters')}
          onCloseFilters={closePopover}
          searchValue={searchValue}
          searchAriaLabel={searchAriaLabel}
          setSearchValue={setSearchValue}
          searchInputRef={searchInputRef}
          handleSearchBlur={handleSearchBlur}
          handleSearchKeyDown={handleSearchKeyDown}
          hasActiveFilters={hasActiveFilters}
          statusOptions={statusOptions}
          statusFilterLabel={statusFilterLabel}
          filterStatuses={filterStatuses}
          onFilterStatusesChange={onFilterStatusesChange}
          filterEntities={filterEntities}
          onFilterEntitiesChange={onFilterEntitiesChange}
          entityOptions={entityOptions}
          entityLabel={entityLabel}
          filterItems={filterItems}
          onFilterItemsChange={onFilterItemsChange}
          itemOptions={itemOptions}
          itemLabel={itemLabel}
          filterSuites={filterSuites}
          onFilterSuitesChange={onFilterSuitesChange}
          suiteOptions={suiteOptions}
          suiteLabel={suiteLabel}
          filterTags={filterTags}
          onFilterTagsChange={onFilterTagsChange}
          availableTags={availableTags}
          filterProjects={filterProjects}
          onFilterProjectsChange={onFilterProjectsChange}
          projectOptions={projectOptions}
          preserveWindowScroll={preserveWindowScroll}
          toolbarInactiveIconColor={toolbarInactiveIconColor}
          tooltipSlotProps={tooltipSlotProps}
        />
      )
    ) : null}
    <PanelHeadingText sx={useWrappedHeaderLayout ? { whiteSpace: 'nowrap' } : undefined}>
      {title}
    </PanelHeadingText>
    {showInlineFilters && !useCollapsedToolbarControls ? (
      <MetricsToolbarFilters
        statusOptions={statusOptions}
        statusFilterLabel={statusFilterLabel}
        filterStatuses={filterStatuses}
        onFilterStatusesChange={onFilterStatusesChange}
        filterEntities={filterEntities}
        onFilterEntitiesChange={onFilterEntitiesChange}
        entityOptions={entityOptions}
        entityLabel={entityLabel}
        filterItems={filterItems}
        onFilterItemsChange={onFilterItemsChange}
        itemOptions={itemOptions}
        itemLabel={itemLabel}
        filterSuites={filterSuites}
        onFilterSuitesChange={onFilterSuitesChange}
        suiteOptions={suiteOptions}
        suiteLabel={suiteLabel}
        filterTags={filterTags}
        onFilterTagsChange={onFilterTagsChange}
        availableTags={availableTags}
        filterProjects={filterProjects}
        onFilterProjectsChange={onFilterProjectsChange}
        projectOptions={projectOptions}
        preserveWindowScroll={preserveWindowScroll}
      />
    ) : null}
    {showInlineFilters && showSearch && !useCollapsedToolbarControls ? (
      <MetricsToolbarSearchControl
        toolbarExpandedSearchWidth={toolbarExpandedSearchWidth}
        toolbarControlSize={toolbarControlSize}
        toolbarSearchWidthPx={toolbarSearchWidthPx}
        searchValue={searchValue}
        searchAriaLabel={searchAriaLabel}
        setSearchValue={setSearchValue}
        searchInputRef={searchInputRef}
        handleSearchBlur={handleSearchBlur}
        handleSearchKeyDown={handleSearchKeyDown}
      />
    ) : null}
  </Stack>
);
